import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { EASE } from "@/components/Motion";

const CHAPTERS = [
  {
    n: "01",
    title: "Source",
    body: "We travel to quarries across Italy, Brazil, Turkey and India, selecting blocks at the face — before they ever become slabs.",
  },
  {
    n: "02",
    title: "Select",
    body: "Every slab is hand-inspected for vein, tone and translucency. Fewer than one in ten makes it into our gallery.",
  },
  {
    n: "03",
    title: "Serve",
    body: "From architects to first-time homeowners, we guide each stone from our yard to your space — cut, finished and installed right.",
  },
];

const SectionHead = () => (
  <>
    <p className="text-[10px] tracking-[0.3em] uppercase text-secondary-foreground mb-4">The manifesto</p>
    <h2 className="font-serif text-3xl font-light tracking-tight leading-[1.05] mb-8">
      Three vows, kept <span className="italic">in stone.</span>
    </h2>
  </>
);

/* Option 1 — swipe slider with snap */
const SwipeSlider = () => (
  <div>
    <SectionHead />
    <div
      className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-5 px-5"
      style={{ scrollbarWidth: "none" }}
      data-testid="manifesto-swipe"
    >
      {CHAPTERS.map((c) => (
        <div key={c.n} className="snap-start shrink-0 w-[80%] border border-border bg-background p-6">
          <p className="font-serif text-5xl font-light text-secondary-foreground/40 mb-4">{c.n}</p>
          <h3 className="font-serif text-xl font-light tracking-tight mb-3">{c.title}</h3>
          <p className="text-sm font-light leading-relaxed text-muted-foreground">{c.body}</p>
        </div>
      ))}
    </div>
    <p className="text-[10px] tracking-[0.25em] uppercase text-secondary-foreground mt-3 text-center">
      Swipe →
    </p>
  </div>
);

/* Option 2 — numbered tabs */
const Tabs = () => {
  const [active, setActive] = useState(0);
  return (
    <div>
      <SectionHead />
      <div className="flex gap-2 mb-6" data-testid="manifesto-tabs">
        {CHAPTERS.map((c, i) => (
          <button
            key={c.n}
            data-testid={`manifesto-tab-${c.n}`}
            onClick={() => setActive(i)}
            className={`flex-1 text-[10px] tracking-[0.15em] uppercase py-3 border transition-colors duration-300 ${
              active === i
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-secondary-foreground"
            }`}
          >
            {c.n} {c.title}
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="border-t border-foreground/15 pt-6"
        >
          <p className="font-serif text-5xl font-light text-secondary-foreground/40 mb-3">
            {CHAPTERS[active].n}
          </p>
          <p className="text-sm font-light leading-relaxed text-muted-foreground">
            {CHAPTERS[active].body}
          </p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

/* Option 3 — accordion */
const Accordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <div>
      <SectionHead />
      <div data-testid="manifesto-accordion">
        {CHAPTERS.map((c, i) => (
          <div key={c.n} className="border-t border-foreground/15 last:border-b">
            <button
              data-testid={`manifesto-acc-${c.n}`}
              onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex items-center justify-between py-5 text-left"
            >
              <span className="flex items-baseline gap-4">
                <span className="font-serif text-2xl font-light text-secondary-foreground/40">{c.n}</span>
                <span className="font-serif text-xl font-light tracking-tight">{c.title}</span>
              </span>
              <Plus
                className={`w-4 h-4 text-secondary-foreground transition-transform duration-300 ${
                  open === i ? "rotate-45" : ""
                }`}
              />
            </button>
            <AnimatePresence initial={false}>
              {open === i && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="overflow-hidden"
                >
                  <p className="text-sm font-light leading-relaxed text-muted-foreground pb-6 pr-8">
                    {c.body}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

function PhoneFrame({ title, note, children }) {
  return (
    <section className="mb-24">
      <div className="mb-6">
        <h2 className="font-serif text-3xl font-light tracking-tight">{title}</h2>
        <p className="mt-2 text-sm font-light text-muted-foreground max-w-xl">{note}</p>
      </div>
      <div className="flex justify-center bg-secondary/30 border border-foreground/20 py-10">
        <div className="w-[360px] max-w-full bg-secondary/40 border border-border p-5 shadow-sm">
          {children}
        </div>
      </div>
    </section>
  );
}

export default function ManifestoOptions() {
  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 pt-40 pb-32" data-testid="manifesto-options-page">
      <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground mb-6">
        Preview — not linked in the menu
      </p>
      <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tighter leading-[0.98] mb-6">
        Manifesto, <span className="italic">pocket-sized.</span>
      </h1>
      <p className="max-w-lg text-base font-light leading-relaxed text-muted-foreground mb-20">
        Three compact ways to show the manifesto on phones. Each preview is real phone width — try
        swiping, tapping. Pick one and I'll wire it into the home page (desktop stays as the
        elegant three-column).
      </p>

      <PhoneFrame
        title="Option 1 — Swipe cards"
        note="One card visible at a time, swipe sideways through the three vows. Most tactile."
      >
        <SwipeSlider />
      </PhoneFrame>

      <PhoneFrame
        title="Option 2 — Numbered tabs"
        note="Tap 01 / 02 / 03 to switch the vow. Cleanest and most compact."
      >
        <Tabs />
      </PhoneFrame>

      <PhoneFrame
        title="Option 3 — Accordion"
        note="All three vows listed; tap a row to expand its text. Familiar and quick to scan."
      >
        <Accordion />
      </PhoneFrame>
    </div>
  );
}
